import type { MissiveComposeErrorDetail } from 'lib/missive/compose-errors';
import { openMissiveInNewTab } from 'lib/missive/open-url';
import type { WhatsAppComposePayload } from 'lib/missive/whatsapp-compose';
import { useCallback, useEffect, useState } from 'react';

import LeadWhatsAppComposeModal from 'components/leads/lead-whatsapp-compose-modal';

import { useSession } from 'providers/session';
import { useUI } from 'providers/ui';

type OpenMissiveWhatsAppOptions = {
  leadId?: string;
  name?: string;
};

type ComposeModalState = {
  phone: string;
  leadId?: string;
  name?: string;
};

function readStoredMissiveOpenError() {
  if (typeof window === 'undefined') {
    return null;
  }

  const raw = localStorage.getItem('cc-missive-open-error');

  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as MissiveComposeErrorDetail & {
      createdAt?: number;
    };

    if (!parsed.createdAt || Date.now() - parsed.createdAt > 2 * 60 * 1000) {
      localStorage.removeItem('cc-missive-open-error');
      return null;
    }

    localStorage.removeItem('cc-missive-open-error');
    return parsed;
  } catch {
    localStorage.removeItem('cc-missive-open-error');
    return null;
  }
}

export function useOpenMissiveWhatsApp() {
  const { token } = useSession();
  const ui = useUI();
  const [opening, setOpening] = useState(false);
  const [composeModal, setComposeModal] = useState<ComposeModalState | null>(
    null,
  );

  useEffect(() => {
    function handleMissiveOpenError(event: MessageEvent) {
      if (event.origin !== window.location.origin) {
        return;
      }

      if (event.data?.type !== 'cc-missive-open-error') {
        return;
      }

      ui.setToast(true, {
        type: 'error',
        title: event.data.title || 'Could not open Missive WhatsApp',
        message: [event.data.message, event.data.hint]
          .filter(Boolean)
          .join(' '),
      });
    }

    window.addEventListener('message', handleMissiveOpenError);

    const storedError = readStoredMissiveOpenError();

    if (storedError) {
      ui.setToast(true, {
        type: 'error',
        title: storedError.title || 'Could not open Missive WhatsApp',
        message: [storedError.message, storedError.hint]
          .filter(Boolean)
          .join(' '),
      });
    }

    return () => {
      window.removeEventListener('message', handleMissiveOpenError);
    };
  }, [ui]);

  const openMissiveWhatsApp = useCallback(
    (phone: string, options?: OpenMissiveWhatsAppOptions | string) => {
      const normalizedPhone = phone.trim();
      const resolvedOptions =
        typeof options === 'string' ? { name: options } : options || {};

      if (!normalizedPhone) {
        return;
      }

      if (!token) {
        ui.setToast(true, {
          type: 'error',
          title: 'Sign in required',
          message: 'Open this lead while signed in to message via Missive.',
        });
        return;
      }

      setComposeModal({
        phone: normalizedPhone,
        leadId: resolvedOptions.leadId,
        name: resolvedOptions.name,
      });
    },
    [token, ui],
  );

  const closeComposeModal = useCallback(() => {
    setComposeModal(null);
  }, []);

  const submitComposeModal = useCallback(
    (whatsapp: WhatsAppComposePayload) => {
      if (!token || !composeModal) {
        return;
      }

      setOpening(true);

      openMissiveInNewTab(
        {
          phone: composeModal.phone,
          name: composeModal.name,
          leadId: composeModal.leadId,
          whatsapp,
        },
        token,
      );

      setComposeModal(null);
      setOpening(false);
    },
    [composeModal, token],
  );

  const WhatsAppComposeModal = composeModal ? (
    <LeadWhatsAppComposeModal
      open={Boolean(composeModal)}
      phone={composeModal.phone}
      leadName={composeModal.name}
      onClose={closeComposeModal}
      onSubmit={submitComposeModal}
    />
  ) : null;

  return {
    openMissiveWhatsApp,
    openingMissiveWhatsApp: opening,
    WhatsAppComposeModal,
  };
}
